import { Format, IsTextColor, TextColor } from "@/utils/formatters";
import { Formatted } from "./formattedValue";
import { displayTransactionType } from "@/helpers/transactions";
import { TransactionType } from "@/api/transactions";
import { useStopwatch } from "react-timer-hook";
import { getSecondsSince } from "@/utils/date";
import { useMemo } from "react";
type ValueProp = {
  value: number;
  inherited?: string;
  suffix?: string;
  prefix?: string;
  decimals?: number;
};

export const Index = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value)} />;
};

export const Investment = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit().currency()} />;
};

export const EntrySpeed = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).time()} />;
};

export const TradeDuration = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).time()} />;
};

export const Age = ({
  value,
  inherited,
  suffix = "",
  prefix = "",
}: ValueProp) => {
  const inheritedColor = useMemo(
    () => (inherited && IsTextColor(inherited) ? inherited : undefined),
    [inherited]
  );

  const secondsSince = useMemo(() => getSecondsSince(value), [value]);

  const { totalSeconds } = useStopwatch({
    interval: secondsSince < 60 ? 1000 : 60000,
  });

  return (
    <Formatted
      value={Format.from(secondsSince + totalSeconds)
        .time({
          variant: "short",
        })
        .format(({ formattedValue }) => {
          return `${prefix}${formattedValue}${suffix}`;
        })
        .color({ inherited: inheritedColor })}
    />
  );
};

export const BuyOrder = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value)} />;
};

export const Pnl = ({ value }: ValueProp) => {
  return (
    <Formatted
      value={Format.from(value)
        .color({ positive: "green2", negative: "red2" })
        .unit()
        .currency()}
      withSign={false}
      withTrendArrow
    />
  );
};

export const PnlInPer = ({ value }: ValueProp) => {
  return (
    <Formatted
      value={Format.from(value)
        .color({ positive: "green2", negative: "red2" })
        .unit()
        .percent()}
      withSign={false}
      withTrendArrow
    />
  );
};

export const WinRate = ({ value }: ValueProp) => {
  return (
    <Formatted
      value={Format.from(value)
        .color({ inherited: "default" })
        .unit()
        .percent()}
      withSign={false}
    />
  );
};

export const Roi = ({ value }: ValueProp) => {
  return (
    <Formatted
      value={Format.from(value)
        .color({ positive: "green2", negative: "red2" })
        .unit()
        .percent()}
      withSign
    />
  );
};

export const TokensSent = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit()} />;
};

export const TokensReceived = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit()} />;
};
export const TokensBought = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit()} />;
};
export const TokensSold = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit()} />;
};

export const Price = ({ value, inherited, decimals = 7 }: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;

  return (
    <Formatted
      value={Format.from(value)
        .unit(value !== 0, decimals, false)
        .currency()
        .color({ inherited: inheritedColor })}
    />
  );
};

export const BotBalance = ({ value, inherited }: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;

  return (
    <Formatted
      value={Format.from(value).unit().color({ inherited: inheritedColor })}
    />
  );
};

export const Time = ({
  value,
  inherited,
  suffix = " ago",
  prefix = "",
}: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;

  return (
    <Formatted
      value={Format.from(value)
        .time({ variant: "short" })
        .format(({ formattedValue }) => {
          return `${prefix}${formattedValue}${suffix}`;
        })
        .color({ inherited: inheritedColor })}
    />
  );
};

export const DateFormat = ({ value, inherited }: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;

  return (
    <Formatted
      value={Format.from(value).date().color({ inherited: inheritedColor })}
    />
  );
};

export const Balance = ({
  value,
  tokenName,
}: {
  value: number;
  tokenName?: string;
}) => {
  const format = Format.from(value).decimal();

  return (
    <Formatted
      value={
        !tokenName
          ? format
          : format.format(({ formattedValue }) => {
              return `${formattedValue} ${tokenName}`;
            })
      }
    />
  );
};

export const Value = ({
  value,
  upliftPercent,
  upliftValue,
  withSign = true,
  withTrendArrow = false,
  inherited,
  percentFirst = true,
  separator,
  positiveNegativeColor = false,
  smallSpace = false,
}: {
  value?: number;
  upliftPercent?: number;
  upliftValue?: number;
  withSign?: boolean;
  withTrendArrow?: boolean;
  inherited?: string;
  percentFirst?: boolean;
  separator?: string;
  positiveNegativeColor?: boolean;
  smallSpace?: boolean;
}) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;
  const color = positiveNegativeColor
    ? { positive: "green2" as TextColor, negative: "red2" as TextColor }
    : { inherited: inheritedColor };

  const formattedValue =
    value !== undefined && value !== null ? (
      <Formatted
        value={Format.from(value as number)
          .unit(value !== 0)
          .currency()
          .color(color)}
        withSign={withSign}
        withTrendArrow={withTrendArrow}
        smallSpace={smallSpace}
      />
    ) : null;

  if (!upliftPercent || !upliftValue) {
    return formattedValue;
  }

  const formattedUpliftPercent = Format.from(upliftPercent)
    .unit()
    .percent()
    .color({
      positive: "green2",
      negative: "red2",
      inherited: inheritedColor,
    });

  const formattedUpliftValue = Format.from(upliftValue)
    .unit()
    .currency()
    .color({
      positive: "green2",
      negative: "red2",
      inherited: inheritedColor,
    });

  if (separator) {
    return (
      <div className="flex flex-row items-center">
        {formattedValue}
        <span className="flex flex-row items-center">
          {percentFirst ? (
            <>
              <Formatted value={formattedUpliftPercent} withSign={withSign} />
              <span className="mx-1" style={{ color: inheritedColor }}>
                {separator}
              </span>
              <Formatted value={formattedUpliftValue} withSign={withSign} />
            </>
          ) : (
            <>
              <Formatted value={formattedUpliftValue} withSign={withSign} />
              <span className="mx-1" style={{ color: inheritedColor }}>
                {separator}
              </span>
              <Formatted value={formattedUpliftPercent} withSign={withSign} />
            </>
          )}
        </span>
      </div>
    );
  }

  //default string is:  +uplifPercent%($upliftValue)
  const formattedPercent = percentFirst
    ? formattedUpliftPercent.format(({ formattedValue }) => {
        return `${formattedValue} (${formattedUpliftValue.toString()})`;
      })
    : formattedUpliftValue.format(({ formattedValue }) => {
        return `${formattedValue} (${formattedUpliftPercent.toString()})`;
      });

  return (
    <div className="flex flex-row items-center">
      {value && formattedValue}
      {formattedPercent && (
        <Formatted value={formattedPercent} withSign={withSign} />
      )}
    </div>
  );
};

export const Transaction = ({ value }: { value: TransactionType }) => {
  return (
    <div className="text-sm font-normal">{displayTransactionType(value)}</div>
  );
};

export const Volume = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit().currency()} />;
};

export const Liquidity = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit().currency()} />;
};

export const MarketCap = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit().currency()} />;
};

export const Amount = ({ value }: ValueProp) => {
  return <Formatted value={Format.from(value).unit()} />;
};

export const Trend = ({
  value,
  withSign = true,
  withTrendArrow = false,
  smallSpace = false,
}: {
  value: number;
  withSign?: boolean;
  withTrendArrow?: boolean;
  smallSpace?: boolean;
}) => {
  return (
    <Formatted
      value={Format.from(value)
        .unit(false, 2)
        .percent()
        .color({ positive: "green2", negative: "red2" })}
      withSign={withSign}
      withTrendArrow={withTrendArrow}
      smallSpace={smallSpace}
    />
  );
};

export const Supply = ({
  value,
  withSign = true,
}: {
  value: number;
  withSign?: boolean;
}) => {
  return (
    <Formatted
      value={Format.from(value).unit(false, 2).percent()}
      withSign={withSign}
    />
  );
};

export const Outflow = ({ value, inherited }: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;
  return (
    <Formatted
      value={Format.from(value).unit().color({ inherited: inheritedColor })}
    />
  );
};

export const Inflow = ({ value, inherited }: ValueProp) => {
  const inheritedColor =
    inherited && IsTextColor(inherited) ? inherited : undefined;
  return (
    <Formatted
      value={Format.from(value).unit().color({ inherited: inheritedColor })}
    />
  );
};
